import { z } from 'zod/v3';

import { BadDataError } from '@watershed/errors/BadDataError';

import { modelParameterTypes } from '../../emissionsModel/emissionsModelSchema';
import pluck from '@watershed/shared-util/src/pluck';

const customizationParameterSchema = z
  .object({
    name: z.string().min(1),
    type: z.enum(modelParameterTypes),
    unit: z.string().nullable().default(null),
    nullable: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.type !== 'number') {
      // Disallow unit for non-number types
      if (data.unit !== null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Unit must be null when type is not 'number'",
          path: ['unit'],
        });
      }
    }
  });

const customizationSchema = z
  .object({
    parameters: z
      .array(customizationParameterSchema)
      // should allow undefined parameters array for backwards compatibility
      .optional()
      .transform((parameters) => (parameters === undefined ? [] : parameters))
      .refine(
        (parameters) => {
          const names = parameters.map(pluck('name'));
          return new Set(names).size === names.length;
        },
        { message: 'Parameter names must be unique' }
      ),
  })
  .strict();

export namespace schemaUtils {
  /**
   * Represents the structured definition (schema) of a calculation
   * customization.
   */
  export type Schema = z.infer<typeof customizationSchema>;

  /**
   * Represents a single parameter in a calculation customization schema.
   */
  export type SchemaParameter = z.infer<typeof customizationParameterSchema>;

  /**
   * Parses unknown input into a validated `Schema` object.
   */
  export function parseSchema(
    schema: unknown
  ): z.SafeParseReturnType<unknown, Schema> {
    return customizationSchema.safeParse(schema);
  }

  /**
   * Utility type that adds a parsed `Schema` field to an existing type.
   */
  export type WithParsedSchema<T, K extends keyof T> = T & {
    [k in K]: Schema;
  };

  /**
   * Takes an object and a key, parses the value at that key as a `Schema`, and
   * returns a new object with the parsed schema attached. Throws `BadDataError`
   * if parsing fails.
   */
  export function withParsedSchema<
    K extends keyof T,
    T extends { [key in K]: object },
  >(obj: T, key: K): WithParsedSchema<T, K> {
    const parseResult = parseSchema(obj[key]);
    if (!parseResult.success) {
      throw new BadDataError('Invalid schema record', {
        cause: parseResult.error,
        data: { parseResult, input: obj },
      });
    }
    return {
      ...obj,
      [key]: parseResult.data,
    };
  }
}
